import { useState } from "react";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";

import MDBox from "components/MDBox";
import { LojaDataGrid } from "layouts/pages/loja/common";
import { endOfDay, isAfter, parseISO } from "date-fns";
import { formatDateEndOfDay } from "helpers/formatDate";
import { formatCurrency } from "helpers/utils";
import { ICupomEvento } from "interfaces/cupom";
import CupomPreviewDialog from "./CupomPreviewDialog";
import MDDialog from "components/MDDialog";
import { CUPONS_ACCENT } from "../cupons.theme";

interface Props {
  cupom: ICupomEvento[];
  onEdit?: (cupom: ICupomEvento) => void;
  onDelete?: (id: number) => void;
}

function cupomExpiration(data: string): JSX.Element {
  const dataExpired = endOfDay(parseISO(data.slice(0, 10)));
  const isExpired = isAfter(new Date(), dataExpired);
  return (
    <Typography variant="body2">
      {formatDateEndOfDay(data, "dd/MM/yyyy")}
      {isExpired && (
        <Typography component="span" variant="caption" color="error.main" sx={{ ml: 0.5 }}>
          (Expirado)
        </Typography>
      )}
    </Typography>
  );
}

const ROW_HEIGHT = 68;
const EVENTO_IMAGE_SIZE = 44;

export default function TableCupomEvento({ cupom, onEdit, onDelete }: Props): JSX.Element {
  const [previewCupom, setPreviewCupom] = useState<ICupomEvento | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; codigo: string } | null>(null);

  const handleDeleteConfirm = async (confirm: boolean) => {
    if (confirm && deleteTarget && onDelete) {
      await onDelete(deleteTarget.id);
    }
    setDeleteTarget(null);
  };

  const columns: GridColDef<ICupomEvento>[] = [
    {
      field: "id",
      headerName: "Evento",
      width: 80,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
          width="100%"
        >
          <Box
            component="img"
            src={params.row.evento.urlImageDestaque}
            alt={params.row.evento.titulo}
            title={params.row.evento.titulo}
            sx={{
              width: EVENTO_IMAGE_SIZE,
              height: EVENTO_IMAGE_SIZE,
              borderRadius: 1,
              objectFit: "cover",
              objectPosition: "center",
              display: "block",
              flexShrink: 0,
            }}
          />
        </MDBox>
      ),
    },
    {
      field: "eventoTitulo",
      headerName: "Nome do evento",
      flex: 1,
      minWidth: 180,
      valueGetter: (params) => params.row.evento?.titulo ?? "",
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} noWrap title={params.row.evento?.titulo}>
          {params.row.evento?.titulo}
        </Typography>
      ),
    },
    {
      field: "codigoPromocional",
      headerName: "Código",
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: "monospace" }}>
          {params.row.codigoPromocional.toUpperCase()}
        </Typography>
      ),
    },
    {
      field: "dataInicio",
      headerName: "Início",
      width: 110,
      renderCell: (params) => formatDateEndOfDay(params.row.dataInicio, "dd/MM/yyyy"),
    },
    {
      field: "validade",
      headerName: "Validade",
      width: 160,
      renderCell: (params) => cupomExpiration(params.row.validade),
    },
    {
      field: "valorCupom",
      headerName: "Valor",
      width: 100,
      renderCell: (params) =>
        params.row.tipoPorcentagem
          ? `${params.row.valorCupom}%`
          : formatCurrency(params.row.valorCupom),
    },
    {
      field: "colorField",
      headerName: "Cor",
      width: 70,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <MDBox
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            bgcolor: params.row.colorField,
            border: "1px solid",
            borderColor: "divider",
          }}
        />
      ),
    },
    {
      field: "qtdCupom",
      headerName: "Usados",
      width: 90,
      headerAlign: "center",
      align: "center",
      renderCell: (params) =>
        `${params.row.qtdCupom - params.row.disponivel} / ${params.row.qtdCupom}`,
    },
    {
      field: "disponivel",
      headerName: "Disp.",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "actions",
      headerName: "",
      width: 130,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          {onEdit && (
            <Tooltip title="Editar cupom">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(params.row);
                }}
              >
                <EditOutlinedIcon fontSize="small" sx={{ color: CUPONS_ACCENT }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Baixar cupom">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setPreviewCupom(params.row);
              }}
            >
              <DownloadOutlinedIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
          {onDelete && (
            <Tooltip title="Excluir cupom">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget({
                    id: params.row.id,
                    codigo: params.row.codigoPromocional.toUpperCase(),
                  });
                }}
              >
                <DeleteOutlineIcon fontSize="small" color="error" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <MDBox>
      <LojaDataGrid
        rows={cupom}
        columns={columns}
        rowHeight={ROW_HEIGHT}
        onRowClick={(params) => onEdit?.(params.row)}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        localeText={{ noRowsLabel: "Nenhum cupom encontrado" }}
        sx={{
          border: "1px solid",
          borderColor: "grey.200",
          borderRadius: 2,
          "& .MuiDataGrid-row": { cursor: onEdit ? "pointer" : "default" },
          "& .MuiDataGrid-row:hover": {
            bgcolor: "rgba(249, 96, 48, 0.04)",
          },
          "& .MuiDataGrid-columnHeaders": {
            bgcolor: "grey.100",
            fontWeight: 700,
          },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },
        }}
      />

      <CupomPreviewDialog
        open={!!previewCupom}
        onClose={() => setPreviewCupom(null)}
        data={previewCupom}
      />

      <MDDialog
        title="Excluir cupom"
        message={`Deseja realmente excluir o cupom "${deleteTarget?.codigo}"? Esta ação não pode ser desfeita.`}
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        response={handleDeleteConfirm}
      />
    </MDBox>
  );
}
